import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Box, Grid, InputAdornment, Paper, Tooltip, Typography } from '@mui/material'

import TextField from '@mui/material/TextField'
import { useSearchForAssetHolderQuery } from '../../../api-services/asset-holdings/assetHolderApi'
import SearchIcon from '@mui/icons-material/Search'
import { RtkQueryHelper } from '../../../api-services/shared/RtkQueryHelper'
import { DataGrid, type GridColumns, type GridValueGetterParams } from '@mui/x-data-grid'
import LoadingComponent from '../../components/ui-widgets/LoadingComponent'
import ErrorComponent from '../../components/ui-widgets/ErrorComponent'
import { type AssetHoldersSearchRequest } from '../../../api-schema-types/asset-holdings/AssetHolderPayloadDto'
import { CountryChip } from '../../components/chips/CountryChip'
import Chip from '@mui/material/Chip'
import { KYCStatusChip } from '../../components/chips/KYCStatusChip'
import { RegistrationStatusChip } from '../../components/chips/RegistrationStatusChip'
import { KYCTierLevelChip } from '../../components/chips/KYCTierLevelChip'
import Button from '@mui/material/Button'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import { selectCurrentAssetHolderId, selectDataGridPage, setDataGridPage } from './editAssetHolderSlice'
import { MemberVIPChip } from '../../components/chips/MemberVIPChip'
import { StartOutlined } from '@mui/icons-material'

const CustomerOperationsPage: React.FC = () => {
  const history = useHistory()
  const dispatch = useAppDispatch()

  const currentAssetHolderId = useAppSelector<number | null>(selectCurrentAssetHolderId)
  const page: number = useAppSelector<number>(selectDataGridPage)
  const createSearchRequest = (search?: string | null): AssetHoldersSearchRequest => {
    if (search === undefined || search === null) {
      return {
        searchInput: ''
      }
    }
    return {
      searchInput: search
    }
  }

  const [searchQuery, setSearchQuery] = useState<AssetHoldersSearchRequest>(createSearchRequest(null))
  const skipQuery = searchQuery.searchInput === ''
  const searchAssetHoldersApiResult = useSearchForAssetHolderQuery(searchQuery, { skip: skipQuery })

  const goTo = (location: string): void => {
    history.push(location)
  }

  const handleSelectedAssetHolder = (assetHolderId: number): void => {
    goTo(`/app/asset-holder/${assetHolderId}`)
  }

  const handleChangePage = (newPage: number): void => {
    dispatch(setDataGridPage(newPage))
  }

  const handleSearchQueryChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const searchQuery: string = event.target.value
    if (searchQuery.length >= 3 || searchQuery.length === 0) {
      setSearchQuery(createSearchRequest(searchQuery))
    }
  }

  const getEditId = (params: GridValueGetterParams): number => {
    return params.row.id
  }

  const columns: GridColumns = [
    {
      field: 'id',
      headerName: 'Id',
      flex: 15,
      editable: false
    },
    {
      field: 'firstName',
      headerName: 'First Name',
      flex: 30,
      renderCell: params => (
          <Tooltip title={params.value}><p>{params.value}</p></Tooltip>
      )
    },
    {
      field: 'lastName',
      headerName: 'Last Name',
      flex: 40,
      renderCell: params => (
          <Tooltip title={params.value}><p>{params.value}</p></Tooltip>
      )
    },
    {
      field: 'username',
      headerName: 'Email Address',
      flex: 60,
      renderCell: params => (
          <Tooltip title={params.value}><p>{params.value}</p></Tooltip>
      )
    },
    {
      field: 'countryCode',
      headerName: 'Country',
      flex: 20,
      editable: false,
      renderCell: params => (
          <CountryChip countryCode={params.value}/>
      )
    },
    {
      field: 'usCitizenStatus',
      headerName: 'US Citizen Status',
      flex: 30,
      editable: false,
      renderCell: params => (
          <Chip label={ params.value.isUsCitizen === true ? 'US Citizen' : 'Non-US Citizen'} color={ params.value.isUsCitizen === true ? 'error' : 'default'} variant="outlined" />
      )
    },
    {
      field: 'kycStatus',
      headerName: 'KYC Status',
      flex: 35,
      editable: false,
      renderCell: params => (
          <KYCStatusChip kycStatus={params.value} />
      )
    },
    {
      field: 'registrationState',
      headerName: 'Registration Status',
      flex: 35,
      editable: false,
      renderCell: params => (
          <RegistrationStatusChip registrationState={params.value} />
      )
    },
    {
      field: 'kycTierLevel',
      headerName: 'KYC Tier',
      flex: 35,
      editable: false,
      renderCell: params => (
          <KYCTierLevelChip kycTierLevel={params.value} />
      )
    },
    {
      field: 'vipMember',
      headerName: 'VIP',
      flex: 20,
      editable: false,
      renderCell: params => (
        <MemberVIPChip vipFlag={params.value} />
      )
    },
    {
      field: 'editId',
      headerName: '',
      flex: 15,
      valueGetter: getEditId,
      renderCell: params => (
          <div>
              <Button variant="text" onClick={() => { handleSelectedAssetHolder(params.value) }}>
                <StartOutlined />
              </Button>
            </div>
      )
    }
  ]

  return (
    <React.Fragment>
      <Grid container>
        <Grid item lg={12}>
          <Grid container sx={{ mb: 4 }} spacing={3}>
            <Grid item lg={6}>
              <Typography variant={'h5'}>Customer Operations</Typography>
            </Grid>
            <Grid item container lg={6} spacing={2} direction="row" alignItems="right" justifyContent="right">
            </Grid>
          </Grid>
          <Paper>
            <Box sx={{ p: 4, mt: 3 }}>
              <TextField
                label="Enter member's proxima id, first name, last name or email address, member wallet address or order reference."
                type='search'
                // value={searchInput}
                sx={{ width: '100%', mb: 2 }}
                placeholder="Email Address"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
                onChange={handleSearchQueryChange}
              />
              {searchAssetHoldersApiResult.isLoading && <LoadingComponent />}
              {(searchAssetHoldersApiResult.error != null) && <ErrorComponent message={RtkQueryHelper.mapResponseError(searchAssetHoldersApiResult.error)} />}
              { searchAssetHoldersApiResult.isSuccess && (
              <DataGrid rows={searchAssetHoldersApiResult.data}
                        columns={columns}
                        autoHeight={true}
                        disableSelectionOnClick
                        getRowClassName={params => {
                          return currentAssetHolderId === params.row.id ? 'Mui-selected' : ''
                        }}
                        pageSize={10}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPageOptions={[10]} />
              )}
            </Box>

            <Box sx={{ pr: 4, pb: 4, pt: 0, textAlign: 'right' }}>

            </Box>
          </Paper>
        </Grid>
      </Grid>
    </React.Fragment>
  )
}

export default CustomerOperationsPage
